import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, UserRole, Permission, ROLE_PERMISSIONS, MOCK_USERS, AuthContextType, UserStatus } from '@/types/users';
import { authApi, saveToken, removeToken, getToken } from '@/lib/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      try {
        // Tentamos buscar o usuário diretamente. O cookie HttpOnly será enviado automaticamente.
        const data = await authApi.me();
        if (data && data.authenticated === false) {
            setUser(null);
            setIsLoading(false);
            return;
        }
        const role = data.role as UserRole;
        const apiUser = {
          ...data,
          role,
          status: (data.status || 'active') as UserStatus,
          permissions: ROLE_PERMISSIONS[role] || [],
          createdAt: new Date(data.created_at),
          updatedAt: new Date(data.updated_at || data.created_at),
          lastLoginAt: data.last_login_at ? new Date(data.last_login_at) : undefined,
        } as User;
        setUser(apiUser);
      } catch (error) {
        // Se der erro (ex: 401 Unauthorized), significa que não há cookie ou ele expirou.
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);

    try {
      // Login via API
      try {
        const data = await authApi.login(email, password);
        // O cookie é definido pelo backend. Não precisamos de saveToken(data.token);

        const role = data.user.role as UserRole;
        const apiUser = {
          ...data.user,
          role,
          status: (data.user.status || 'active') as UserStatus,
          permissions: ROLE_PERMISSIONS[role] || [],
          createdAt: new Date(data.user.created_at),
          updatedAt: new Date(data.user.updated_at || data.user.created_at),
          lastLoginAt: new Date(),
        } as User;

        setUser(apiUser);
      } catch (apiError: unknown) {
        throw new Error('Credenciais inválidas ou API indisponível');
      }
    } catch (error) {
      console.error('Erro no login:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('[AUTH] Erro ao fazer logout:', err);
    }
    setUser(null);
  };

  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;
    return user.permissions.includes(permission);
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    if (!user) return false;
    return permissions.some(permission => user.permissions.includes(permission));
  };

  const hasRole = (role: UserRole): boolean => {
    if (!user) return false;
    return user.role === role;
  };

  const updateProfile = async (data: Partial<User>): Promise<void> => {
    if (!user) throw new Error('Usuário não autenticado');

    try {
      const updatedUser = {
        ...user,
        ...data,
        updatedAt: new Date()
      } as User;

      setUser(updatedUser);
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      throw error;
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const data = await authApi.me();
      if (data && data.authenticated === false) {
          setUser(null);
          return;
      }
      const role = data.role as UserRole;
      const apiUser = {
        ...data,
        role,
        status: (data.status || 'active') as UserStatus,
        permissions: ROLE_PERMISSIONS[role] || [],
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at || data.created_at),
      } as User;
      setUser(apiUser);
    } catch (error) {
      console.error('Erro ao atualizar dados do usuário:', error);
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    permissions: user ? user.permissions : [],
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    hasRole,
    updateProfile,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};